import { z } from 'zod'
import { FieldView } from './sourceView.js'
import { FieldId } from './id.js'

/**
 * Wat een verwijzing oplevert, in de vorm die naar de client gaat (R-54, R-61, R-63).
 *
 * **Waarom dit niet in de projectie zit.** De projectie draagt de vorm en de rechten en met
 * opzet geen waarden: in de bewerkbare weergaven komen die uit de sectiedocumenten en in de
 * alleen-lezen weergaven uit de uitgeschreven revisie (`projectie.ts`, ontwerp 6.8 en 4.5). Een
 * verwijzing heeft geen van beide kanalen, want een bronsectie krijgt geen sectiedocument en
 * niemand schrijft haar (R-56). Ze heeft dus een eigen kanaal nodig, en dit is het.
 *
 * **Waarom er bij `onbeschikbaar` geen reden staat, en dat is de kern van R-63.** Zie het
 * besluit onder M6.0 in `docs/dekking.md`: de pod lost een verwijzing alleen op uit de
 * dossierdocumenten die hij van de gastheer heeft meegekregen, en verzint nooit een waarde die
 * er niet is. Welke documenten meereizen is de beslissing van de gastheer bij het uitzetten,
 * want daar is de autorisatie van deze invuller bekend.
 *
 * Zou hier een reden staan, dan lekt precies wat R-63 beschermt: het verschil tussen "dat blok
 * heeft niets op dit dossier achtergelaten" en "er staat iets maar jij mag het niet zien" is
 * zelf informatie over een ander bouwblok. Daarom is dit een `strictObject` zonder `reden`: de
 * engine kent de reden wel en zet hem in het log (repo-afspraak: naar buiten een melding die de
 * invuller begrijpt, in het log de volledige keten), en dit schema maakt het structureel
 * onmogelijk hem per ongeluk mee te sturen. Er staat een test op.
 */
export const VerwijzingOutcome = z.discriminatedUnion('stand', [
  z.strictObject({
    stand: z.literal('waarde'),
    /**
     * Wat er in het dossierdocument stond, ongewijzigd.
     *
     * `unknown` en geen scalair: een pad mag ook een hele sectie of een lijst aanwijzen (R-55),
     * en dat is precies wat een bronsectie nodig heeft om de reeks van R-62 te tonen. Wat de
     * renderer met welke vorm doet is een vraag voor de control en niet voor dit schema.
     */
    waarde: z.unknown(),
    /**
     * Hoe het bronveld eruitzag voor wie het invulde: label, soort, keuzes, kolommen (R-55,
     * R-100, R-143). Uit de publicatie van de stand waaruit `waarde` komt, bevroren op de
     * instantie (`SourceView`). Ontbreekt hij, dan toont de lezer de kale waarde: een stand
     * van buiten dit product, of een instantie van vóór de bevriezing.
     *
     * Alleen bij een waarde: bij `onbeschikbaar` gaat er niets over de bron mee (R-63).
     */
    weergave: FieldView.optional(),
  }),
  z.strictObject({ stand: z.literal('onbeschikbaar') }),
])
export type VerwijzingOutcome = z.infer<typeof VerwijzingOutcome>

/**
 * De opgeloste verwijzingen van één projectie, per veld.
 *
 * Alleen de velden die in de projectie staan komen erin voor. Dat is de eerste helft van R-63 en
 * ze komt gratis: een veld in een sectie waar deze lezer geen recht op heeft staat niet in de
 * projectie, dus er valt niets voor op te lossen.
 */
export const Verwijzingen = z.record(FieldId, VerwijzingOutcome)
export type Verwijzingen = z.infer<typeof Verwijzingen>
